import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Shield, ShieldAlert, CheckCircle2, Clock, AlertTriangle, Check,
    Copy, Search, Filter, RefreshCw, Terminal, ExternalLink,
    ChevronRight, X, ArrowRight, UserCheck, ShieldCheck, Flame,
    Layers, Plus, Trash2, ArrowUpRight, Cpu
} from "lucide-react";

export default function FirewallApprovals() {
    const navigate = useNavigate();
    const [approvalsData, setApprovalsData] = useState([]);
    const [counts, setCounts] = useState({ pending: 0, applied: 0, rejected: 0 });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("pending"); // "pending" | "applied" | "all"
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedGateway, setSelectedGateway] = useState("ALL");
    const [copiedId, setCopiedId] = useState(null);
    const [toast, setToast] = useState(null);
    const [actionLoading, setActionLoading] = useState({});
    
    // Modal for staging a new manual firewall change
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newChangeForm, setNewChangeForm] = useState({
        targetEntity: "",
        targetGateway: "BCS-80-WAN-FW-01",
        actionType: "BLOCK_IP",
        incidentName: "Manual SOC Containment Request"
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchApprovals();
    }, []);

    const fetchApprovals = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/firewall/approvals`, { headers });
            setApprovalsData(res.data.approvals || []);
            setCounts(res.data.counts || { pending: 0, applied: 0, rejected: 0 });
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load firewall approvals:", err);
        }
        setLoading(false);
    };

    const fallbackCopy = (text, id) => {
        try {
            const textArea = document.createElement("textarea");
            textArea.value = text;
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand("copy");
            textArea.remove();
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch (e) {
            console.error("Fallback copy failed:", e);
        }
    };

    const handleCopyCli = (text, id) => {
        if (!text) return;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text)
                .then(() => {
                    setCopiedId(id);
                    setTimeout(() => setCopiedId(null), 2000);
                })
                .catch(() => fallbackCopy(text, id));
        } else {
            fallbackCopy(text, id);
        }
    };

    const handleApplyOnFirewall = async (id) => {
        setActionLoading(prev => ({ ...prev, [id]: true }));
        try {
            const res = await axios.patch(
                `${backendURL}/api/firewall/approvals/${id}/apply`,
                { notes: "Verified and applied directly on FortiGate perimeter gateway." },
                { headers }
            );

            if (res.data.success) {
                setApprovalsData(prev =>
                    prev.map(a => a.id === id ? { ...a, status: "APPLIED_ON_FIREWALL", reviewedBy: res.data.reviewedBy, reviewedAt: res.data.reviewedAt } : a)
                );
                setCounts(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1), applied: prev.applied + 1 }));
                setToast(`✓ Change #${id} marked as Applied & Enforced on Firewall!`);
                setTimeout(() => setToast(null), 4000);
            }
        } catch (err) {
            console.error("Failed to apply approval:", err);
        }
        setActionLoading(prev => ({ ...prev, [id]: false }));
    };

    const handleRejectApproval = async (id) => {
        setActionLoading(prev => ({ ...prev, [id]: true }));
        try {
            const res = await axios.patch(
                `${backendURL}/api/firewall/approvals/${id}/reject`,
                { reason: "Dismissed during SOC administrator review." },
                { headers }
            );

            if (res.data.success) {
                setApprovalsData(prev =>
                    prev.map(a => a.id === id ? { ...a, status: "REJECTED" } : a)
                );
                setCounts(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1), rejected: prev.rejected + 1 }));
                setToast(`Change #${id} rejected and dismissed.`);
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to reject approval:", err);
        }
        setActionLoading(prev => ({ ...prev, [id]: false }));
    };

    const handleCreateChangeRequest = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${backendURL}/api/firewall/approvals`, newChangeForm, { headers });
            if (res.data.success) {
                setApprovalsData(prev => [res.data.approval, ...prev]);
                setCounts(prev => ({ ...prev, pending: prev.pending + 1 }));
                setCreateModalOpen(false);
                setNewChangeForm({
                    targetEntity: "",
                    targetGateway: "BCS-80-WAN-FW-01",
                    actionType: "BLOCK_IP",
                    incidentName: "Manual SOC Containment Request"
                });
                setToast("New Firewall Change Staged for Approval!");
                setTimeout(() => setToast(null), 3500);
            }
        } catch (err) {
            console.error("Failed to stage change:", err);
        }
    };

    const baseGateways = [
        "BCS-80-WAN-FW-01",
        "Querytel-DataCanter",
        "SunnySide",
        "KingTravels",
        "Al-Musaab",
        "FritzMarketing",
        "Musaab-Home",
        "CAL-WAN-FW"
    ];
    const gatewaysList = Array.from(new Set([...baseGateways, ...approvalsData.map(a => a.targetGateway).filter(Boolean)]));

    const filteredApprovals = approvalsData.filter(item => {
        const matchesTab =
            activeTab === "all" ? true :
            activeTab === "pending" ? item.status === "PENDING_APPROVAL" :
            item.status === "APPLIED_ON_FIREWALL";

        const matchesGateway = selectedGateway === "ALL" || item.targetGateway === selectedGateway;
        const q = (searchQuery || "").toLowerCase();
        const matchesSearch = !q ||
            (item.title || "").toLowerCase().includes(q) ||
            (item.targetEntity || "").toLowerCase().includes(q) ||
            (item.id || "").toLowerCase().includes(q) ||
            (item.incidentName || "").toLowerCase().includes(q) ||
            (item.targetGateway || "").toLowerCase().includes(q);

        return matchesTab && matchesGateway && matchesSearch;
    });

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-emerald-600/95 backdrop-blur-md text-white px-4 py-3 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-200 border border-emerald-400/30">
                    <CheckCircle2 className="w-4 h-4 text-white" />
                    <span>{toast}</span>
                </div>
            )}

            {/* ─── Breadcrumb & Top Bar ─── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-[#111827] border border-white/[0.06] rounded-xl px-5 py-3.5 shadow-sm">
                <div className="flex items-center gap-2 text-[11px] font-mono text-slate-400">
                    <span className="text-slate-500 font-bold">03</span>
                    <span>/</span>
                    <span className="text-white font-bold">Firewall Change & Approval Queue</span>
                    <span className="ml-2 text-[9.5px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded border border-indigo-500/30 font-bold">
                        SOC Change Control
                    </span>
                </div>

                <div className="flex items-center gap-2.5">
                    <button
                        onClick={fetchApprovals}
                        className="p-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11px] transition-colors flex items-center gap-1.5"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin text-blue-400" : ""}`} />
                        <span>Refresh</span>
                    </button>
                    <button
                        onClick={() => setCreateModalOpen(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all whitespace-nowrap"
                    >
                        <Plus className="w-3.5 h-3.5" />
                        Stage Firewall Change
                    </button>
                </div>
            </div>

            {/* ─── KPI Metrics Row ─── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#111827] border border-amber-500/20 rounded-xl p-4 space-y-2 shadow-sm relative overflow-hidden">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-mono text-amber-400 font-bold uppercase tracking-wider">
                            Pending Admin Review
                        </span>
                        <div className="w-7 h-7 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400">
                            <Clock className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-[26px] font-black text-white font-mono">{counts.pending}</span>
                        <span className="text-[11px] text-amber-300/80 font-medium">Ready to deploy</span>
                    </div>
                    <p className="text-[11px] text-slate-400">Awaiting admin verification & FortiOS push</p>
                </div>

                <div className="bg-[#111827] border border-emerald-500/20 rounded-xl p-4 space-y-2 shadow-sm relative overflow-hidden">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-mono text-emerald-400 font-bold uppercase tracking-wider">
                            Applied & Enforced
                        </span>
                        <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                            <ShieldCheck className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-[26px] font-black text-white font-mono">{counts.applied}</span>
                        <span className="text-[11px] text-emerald-300/80 font-medium">Active on Hardware</span>
                    </div>
                    <p className="text-[11px] text-slate-400">Enforced on FortiGate perimeter edge</p>
                </div>

                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-4 space-y-2 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-mono text-slate-400 font-bold uppercase tracking-wider">
                            FortiGate Gateways
                        </span>
                        <div className="w-7 h-7 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                            <Flame className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-[26px] font-black text-white font-mono">8</span>
                        <span className="text-[11px] text-slate-400 font-medium">Monitored</span>
                    </div>
                    <p className="text-[11px] text-slate-400">BCS-80, DataCenter, SunnySide, etc.</p>
                </div>

                <div className="bg-[#111827] border border-white/[0.06] rounded-xl p-4 space-y-2 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-[11px] font-mono text-slate-400 font-bold uppercase tracking-wider">
                            Avg Approval Time
                        </span>
                        <div className="w-7 h-7 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400">
                            <UserCheck className="w-4 h-4" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-2">
                        <span className="text-[26px] font-black text-white font-mono">1.8m</span>
                        <span className="text-[11px] text-indigo-300 font-medium">Fast Containment</span>
                    </div>
                    <p className="text-[11px] text-slate-400">Zero-lag change validation</p>
                </div>
            </div>

            {/* ─── Filter & Queue Workbench ─── */}
            <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-6">
                
                {/* Tabs & Controls */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.06] pb-4">
                    
                    {/* View Switcher Tabs */}
                    <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-xl p-1 text-[11.5px] font-bold text-slate-400">
                        <button
                            onClick={() => setActiveTab("pending")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                activeTab === "pending"
                                    ? "bg-amber-500/20 text-amber-300 border border-amber-500/30 shadow-sm"
                                    : "hover:text-slate-200"
                            }`}
                        >
                            <Clock className="w-3.5 h-3.5 text-amber-400" />
                            Pending Approvals
                            <span className="px-1.5 py-0.2 rounded-full bg-amber-500/30 text-amber-200 text-[10px] font-mono font-bold">
                                {counts.pending}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab("applied")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                activeTab === "applied"
                                    ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm"
                                    : "hover:text-slate-200"
                            }`}
                        >
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                            Applied on Firewall
                            <span className="px-1.5 py-0.2 rounded-full bg-emerald-500/30 text-emerald-200 text-[10px] font-mono font-bold">
                                {counts.applied}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab("all")}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                                activeTab === "all"
                                    ? "bg-blue-600 text-white shadow-sm"
                                    : "hover:text-slate-200"
                            }`}
                        >
                            All Changes ({approvalsData.length})
                        </button>
                    </div>

                    {/* Search & Gateway Filter */}
                    <div className="flex items-center gap-3">
                        <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 w-64 focus-within:border-blue-500/40">
                            <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                            <input
                                type="text"
                                placeholder="Search IP, ticket, gateway..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                            />
                        </div>

                        <select
                            value={selectedGateway}
                            onChange={(e) => setSelectedGateway(e.target.value)}
                            className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-lg px-3 py-2 outline-none cursor-pointer"
                        >
                            <option value="ALL">All Firewalls ({gatewaysList.length} Gateways)</option>
                            {gatewaysList.map((gw, idx) => (
                                <option key={idx} value={gw}>{gw}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Queue Cards List */}
                {loading ? (
                    <div className="py-16 text-center text-slate-400 font-mono text-[13px] flex flex-col items-center gap-2">
                        <RefreshCw className="w-6 h-6 animate-spin text-blue-400" />
                        <span>Loading Firewall Approval Queue...</span>
                    </div>
                ) : filteredApprovals.length === 0 ? (
                    <div className="py-16 text-center text-slate-400 border border-dashed border-white/[0.08] rounded-xl space-y-2">
                        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
                        <p className="text-[14px] font-bold text-white">No Approval Requests in this View</p>
                        <p className="text-[12px] text-slate-500">All pending changes have been reviewed and applied to FortiGate firewalls.</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredApprovals.map((item) => {
                            const isPending = item.status === "PENDING_APPROVAL";
                            const isApplied = item.status === "APPLIED_ON_FIREWALL";
                            const isRejected = item.status === "REJECTED";
                            const isCopied = copiedId === item.id;
                            const isWorking = actionLoading[item.id];

                            return (
                                <div
                                    key={item.id}
                                    className={`p-5 rounded-xl border transition-all duration-200 space-y-4 ${
                                        isPending
                                            ? "bg-[#161F30] border-amber-500/30 shadow-lg"
                                            : isApplied
                                            ? "bg-black/30 border-white/[0.06]"
                                            : "bg-black/20 border-red-500/20 opacity-70"
                                    }`}
                                >
                                    {/* Card Header */}
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                                        <div className="flex items-start gap-3">
                                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                                isPending
                                                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                                    : isApplied
                                                    ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                                    : "bg-red-500/20 text-red-400 border border-red-500/30"
                                            }`}>
                                                {isPending ? <Clock className="w-4 h-4" /> : isApplied ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                                            </div>

                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-[11px] font-mono font-bold text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">
                                                        {item.id}
                                                    </span>
                                                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20 font-bold">
                                                        {item.targetGateway}
                                                    </span>
                                                    <span className={`text-[9.5px] font-mono uppercase px-2 py-0.5 rounded font-bold ${
                                                        isPending ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" :
                                                        isApplied ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" :
                                                        "bg-red-500/20 text-red-300 border border-red-500/30"
                                                    }`}>
                                                        {isPending ? "Pending Admin Approval" : isApplied ? "✓ Applied & Enforced on Firewall" : "Dismissed"}
                                                    </span>
                                                </div>
                                                <h4 className="text-[15px] font-extrabold text-white leading-snug">
                                                    {item.title}
                                                </h4>
                                                <p className="text-[12px] text-slate-400">
                                                    Target IOC: <strong className="text-white font-mono">{item.targetEntity}</strong> • Triggered by: <span className="text-slate-300">{item.requestedBy}</span> • Incident: <Link to="/incidents" className="text-blue-400 hover:underline">{item.incidentId}</Link>
                                                </p>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex items-center gap-2 self-end lg:self-auto flex-shrink-0">
                                            <button
                                                onClick={() => handleCopyCli(item.fortiosCli, item.id)}
                                                className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white rounded-lg text-[11px] font-bold transition-colors flex items-center gap-1.5 border border-white/[0.06]"
                                                title="Copy FortiOS CLI Script"
                                            >
                                                {isCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                                                <span>{isCopied ? "CLI Copied!" : "Copy FortiOS CLI"}</span>
                                            </button>

                                            {isPending && (
                                                <>
                                                    <button
                                                        onClick={() => handleApplyOnFirewall(item.id)}
                                                        disabled={isWorking}
                                                        className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                                                    >
                                                        {isWorking ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                                        <span>Mark as Applied on Firewall</span>
                                                    </button>
                                                    <button
                                                        onClick={() => handleRejectApproval(item.id)}
                                                        disabled={isWorking}
                                                        className="p-1.5 bg-white/[0.04] hover:bg-red-600/20 text-slate-400 hover:text-red-300 rounded-lg text-[11px] transition-colors border border-white/[0.06] cursor-pointer"
                                                        title="Dismiss / Reject Change"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </>
                                            )}

                                            {isApplied && (
                                                <div className="text-right text-[11px] font-mono text-emerald-400">
                                                    <span>Enforced by {item.reviewedBy || "Admin"}</span>
                                                    <span className="block text-[10px] text-slate-500">{new Date(item.reviewedAt).toLocaleString()}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* CLI Script Preview */}
                                    <div className="space-y-1.5 pt-1">
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5 font-mono">
                                                <Terminal className="w-3.5 h-3.5 text-indigo-400" />
                                                FortiOS CLI Configuration Script (Ready for FortiGate Gateway)
                                            </span>
                                            <span className="text-[10px] text-slate-500 font-mono">FortiOS 7.2+ / FortiGate CLI</span>
                                        </div>
                                        <pre className="p-3.5 bg-black/60 border border-white/[0.06] rounded-xl text-[11.5px] font-mono text-emerald-400 overflow-x-auto leading-relaxed">
                                            {item.fortiosCli}
                                        </pre>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ─── Modal: Stage New Firewall Change ─── */}
            {createModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Stage Firewall Change for Admin Review</h3>
                            <button onClick={() => setCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateChangeRequest} className="space-y-3.5 text-[12px]">
                            <div>
                                <label className="text-slate-400 block mb-1">Target Entity (IP / Subnet / Domain)</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. 10.10.56.69 or malicious-domain.com"
                                    value={newChangeForm.targetEntity}
                                    onChange={(e) => setNewChangeForm({ ...newChangeForm, targetEntity: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-blue-500 font-mono"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Target FortiGate Gateway</label>
                                    <select
                                        value={newChangeForm.targetGateway}
                                        onChange={(e) => setNewChangeForm({ ...newChangeForm, targetGateway: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        {gatewaysList.map((gw, idx) => (
                                            <option key={idx} value={gw}>{gw}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="text-slate-400 block mb-1">Action Type</label>
                                    <select
                                        value={newChangeForm.actionType}
                                        onChange={(e) => setNewChangeForm({ ...newChangeForm, actionType: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="BLOCK_IP">Perimeter IP Zero-Trust Block</option>
                                        <option value="QUARANTINE_HOST">Quarantine Host at Gateway</option>
                                        <option value="BLOCK_DOMAIN">Web Filter Domain Block</option>
                                        <option value="GEO_IP_BLOCK">Inbound Geo-IP Block</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Incident / Reason Description</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Containment for C2 beaconing detected by FortiAnalyzer"
                                    value={newChangeForm.incidentName}
                                    onChange={(e) => setNewChangeForm({ ...newChangeForm, incidentName: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setCreateModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Stage Change Request
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
